import PropTypes from 'prop-types';
import {
    TextInput,
    BooleanInput,
    SimpleForm,
    required,
    maxLength,
    SaveButton,
    Toolbar,
    SelectInput,
    ReferenceInput,
    AutocompleteInput,
    usePermissions,
    useTranslate,
    useRecordContext,
} from 'react-admin';
import { DateInput } from '@/components/global/inputs/DateInput.jsx';
import { Grid, Box, Typography } from '@mui/material';
import { collectionTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { DeviceRelationInputs } from '@/components/device/forms/DeviceRelationInputs.jsx';
import { deviceModelFilterToQuery } from '@/helpers/filters/filterToQuery.js';
import { getDeviceModelOptionText } from '@/helpers/parameter/parameterSubsetDisplay.js';

export const DeviceForm = ({ EditInputs }) => {
    const record = useRecordContext();
    const { isLoading, permissions } = usePermissions();
    const translate = useTranslate();
    if (isLoading) return null;

    const toolbar = (
        <Toolbar>
            <SaveButton />
        </Toolbar>
    );

    return (
        <SimpleForm mode='onBlur' reValidateMode='onBlur' toolbar={toolbar}>
            <Grid container>
                <Grid item sm={12}>
                    {EditInputs}
                    <Typography variant='h6' gutterBottom>
                        {translate('common.details')}
                    </Typography>
                    <DeviceRelationInputs />
                    <TextInput
                        source='description'
                        label={translate('common.description')}
                        fullWidth={true}
                        multiline
                        validate={maxLength(500, translate('common.validation.maxLength', { count: 500 }))}
                    />
                    <TextInput source='serialNumber' label={translate('resources.device.fields.serialNumber')} fullWidth={true} disabled />

                    <SelectInput
                        source='collectionType'
                        name='collectionType'
                        label={translate('resources.device.fields.collectionType')}
                        choices={collectionTypeChoices(translate)}
                        fullWidth
                        validate={required(translate('common.validation.required'))}
                    />
                    {permissions.isAdmin && (
                        <Box className='autocomplete-parent'>
                            <ReferenceInput source='modelId' reference='device-model' perPage={25} sort={{ field: 'code', order: 'ASC' }}>
                                <AutocompleteInput
                                    label={translate('common.model')}
                                    optionText={getDeviceModelOptionText}
                                    filterToQuery={deviceModelFilterToQuery}
                                    validate={required(translate('common.validation.required'))}
                                    fullWidth
                                    disablePortal
                                />
                            </ReferenceInput>
                        </Box>
                    )}
                    <DateInput
                        className='date-input'
                        source='productionDate'
                        label={translate('resources.device.fields.productionDate')}
                        fullWidth={true}
                        disabled={!permissions.isAdmin}
                        validate={required(translate('common.validation.required'))}
                    />
                    <DateInput
                        className='date-input'
                        source='deploymentDate'
                        label={translate('resources.device.fields.deploymentDate')}
                        fullWidth={true}
                        disabled={!permissions.isAdmin}
                        validate={required(translate('common.validation.required'))}
                    />
                    <DateInput
                        className='date-input'
                        source='warrantyDate'
                        label={translate('resources.device.fields.warrantyDate')}
                        name='warrantyDate'
                        fullWidth
                        disabled={!permissions.isAdmin}
                    />
                    <Typography variant='h6' gutterBottom>
                        {translate('menu.software')}
                    </Typography>
                    <Box display='flex' mb='2em'>
                        <Box flex={1} mr='1em'>
                            <TextInput source='softwareSoc' label={translate('resources.device.labels.soc')} fullWidth={true} disabled />
                        </Box>
                        <Box flex={1} mr='1em'>
                            <TextInput source='softwareMcu' label={translate('resources.device.labels.mcu')} fullWidth={true} disabled />
                        </Box>
                    </Box>
                    <Typography variant='h6' gutterBottom>
                        {translate('resources.device.labels.hardware')}
                    </Typography>
                    <Box display='flex' mb='2em'>
                        <Box flex={1} mr='1em'>
                            <TextInput source='hardwareSoc' label={translate('resources.device.labels.soc')} fullWidth={true} disabled />
                        </Box>
                        <Box flex={1} mr='1em'>
                            <TextInput source='hardwareMcu' label={translate('resources.device.labels.mcu')} fullWidth={true} disabled />
                        </Box>
                    </Box>
                    <Box display='flex' mb='2em' sx={{ paddingLeft: '6px' }}>
                        <Box flex={1} mr='1em'>
                            <BooleanInput source='isActive' size='small' label={translate('common.active')} disabled={!permissions.isAdmin} />
                        </Box>
                        <Box flex={1} mr='1em'>
                            <BooleanInput
                                source='isRestart'
                                size='small'
                                label={translate('common.deviceActions.restart.label')}
                                disabled={!permissions.isAdmin}
                            />
                        </Box>
                    </Box>
                    {'Kaucja' === record?.collectionPoint?.operator && (
                        <Box display='flex' mb='2em' sx={{ paddingLeft: '6px' }}>
                            <Box flex={1} mr='1em'>
                                <BooleanInput
                                    label={translate('resources.device.fields.markTransactionAsExchanged')}
                                    source='markTransactionAsExchanged'
                                    size='small'
                                    disabled={!permissions.isAdmin}
                                />
                            </Box>
                        </Box>
                    )}
                </Grid>
            </Grid>
        </SimpleForm>
    );
};

DeviceForm.propTypes = {
    EditInputs: PropTypes.node,
};
